/**
 * «Non coperto» — what a proposal line leaves of what the door asked for, and why (bata-ui#302 B).
 *
 * The pull computes a NEED per store × article × size (`requested_units`: the target less what the
 * door holds and has on the way, floored at nought), and the engine then proposes what it can
 * (`units`). The gap is the need the run did not cover on that line:
 *
 *   uncovered = max(0, requested − proposed)
 *
 * the digest's own `unmet_units` per line (ADR 9015), so the two never disagree.
 *
 * **Why.** Platform ADR 6038 decomposes every line into signed drivers in pairs: six build the need
 * (forecast, service buffer, presentation minimum, on hand, in transit, whole units) and exactly one
 * of the last three is the ENGINE's change to it — case rounding, DC availability, or the
 * allocation's own ranking. A line the engine cut has that driver negative, and its value is the
 * cut. So the reason is the negative engine driver, in the driver's own words, with its pairs:
 * «Stock del centro di distribuzione esaurito: −1». Nothing is inferred: a line whose run published
 * no decomposition says so, and a cut no engine driver accounts for says that, beside its `bound_by`.
 *
 * Only a line the run WROTE has a gap here. A door × size the run proposed nothing for — need zero,
 * a door excluded, a pool that ran dry before it — has no line, and so no row on this page; the
 * network-health cockpit is where those are counted (and, once the platform publishes it, where
 * each says why).
 *
 * Pure.
 */

import type { ProposalSummary } from '@/api/generated';

import { toDriverReason } from './explain';
import { displayNumberFormat } from '@/lib/format';

/**
 * The driver codes that are the engine's change to the need (platform ADR 6038), and the pairs the
 * transport cost took off after the solve (`shipment_cost`, ADR 6188).
 */
export const ENGINE_DRIVER_CODES: ReadonlySet<string> = new Set(['case_rounding', 'dc_availability', 'allocation', 'shipment_cost']);

/** One reason a line is short, in pairs (negative: the cut). */
export interface UncoveredReason {
  readonly code: string;
  readonly labelKey: string;
  readonly labelValues?: Readonly<Record<string, string | number>>;
  readonly contribution: number;
}

export type LineCoverage =
  /** The line covers what the door asked for (or more: a presentation floor can raise it). */
  | { readonly kind: 'covered' }
  /** The line is short, and the engine drivers that cut it — at least one. */
  | { readonly kind: 'short'; readonly units: number; readonly reasons: readonly UncoveredReason[] }
  /** The line is short, and the run published no decomposition to say why. */
  | { readonly kind: 'short-unexplained'; readonly units: number; readonly why: 'no-decomposition' | 'no-engine-driver' };

/** Pairs asked for and not proposed on the line — never negative. */
export function uncoveredUnits(line: Pick<ProposalSummary, 'requested_units' | 'units'>): number {
  return Math.max(0, line.requested_units - line.units);
}

/** The line's coverage and, when short, why. Pure. */
export function lineCoverage(line: Pick<ProposalSummary, 'requested_units' | 'units' | 'drivers'>): LineCoverage {
  const units = uncoveredUnits(line);
  if (units === 0) return { kind: 'covered' };
  const drivers = line.drivers ?? [];
  if (drivers.length === 0) return { kind: 'short-unexplained', units, why: 'no-decomposition' };
  const reasons = drivers
    .filter((driver) => ENGINE_DRIVER_CODES.has(driver.code) && driver.contribution < 0)
    .sort((a, b) => a.contribution - b.contribution || a.code.localeCompare(b.code))
    .map((driver): UncoveredReason => {
      const reason = toDriverReason(driver);
      return {
        code: driver.code, labelKey: reason.labelKey, contribution: driver.contribution,
        ...(reason.labelValues === undefined ? {} : { labelValues: reason.labelValues }),
      };
    });
  return reasons.length === 0 ? { kind: 'short-unexplained', units, why: 'no-engine-driver' } : { kind: 'short', units, reasons };
}

/** The filter value of a line: `uncovered` when it is short, else `covered`. */
export const COVERAGE_UNCOVERED = 'uncovered';
export const COVERAGE_COVERED = 'covered';

export function coverageFilterValue(line: Pick<ProposalSummary, 'requested_units' | 'units'>): string {
  return uncoveredUnits(line) > 0 ? COVERAGE_UNCOVERED : COVERAGE_COVERED;
}

/** A signed pair count as the reason shows it: «−1», «+2». */
export function signedPairs(value: number, locale: string): string {
  return displayNumberFormat(locale, { maximumFractionDigits: 0, signDisplay: 'exceptZero' }).format(value);
}

/**
 * The reason as one line of words, for a file cell or a cell's accessible name: each engine driver
 * «{label}: {pairs}», or why there is none. `translate` resolves a catalogue key.
 */
export function coverageWords(
  coverage: LineCoverage,
  translate: (key: string, values?: Readonly<Record<string, unknown>>) => string,
  locale: string,
): string | null {
  if (coverage.kind === 'covered') return null;
  if (coverage.kind === 'short-unexplained') {
    return translate(coverage.why === 'no-decomposition' ? 'inseason.uncovered.noDecomposition' : 'inseason.uncovered.noEngineDriver');
  }
  return coverage.reasons
    .map((reason) => translate('inseason.uncovered.reason', {
      label: translate(reason.labelKey, reason.labelValues ?? {}),
      pairs: signedPairs(reason.contribution, locale),
    }))
    .join('; ');
}
